import React, { FC } from 'react';
import { BlurWrapper } from 'ui';
import { CompanyPanel } from 'ui/Company';
import { Typography, Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { ScoreWidget, ScoreWidgetMainColumn, ScoreWidgetSingleValueColumn } from 'ui/ScoreWidget';
import { RequestEsgScoreModal } from 'modules/common/Modals';
import { usePortfolioModals } from '../../shared/hooks/usePortfolioModals/usePortfolioModals';
import { RatingStatus } from '../../shared/types/ratingStatus';

const useStyles = makeStyles(() => ({
  requestBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    height: '100%',
    paddingLeft: '25px',
    paddingRight: '25px',
    whiteSpace: 'break-spaces',
    textAlign: 'center',
  },
}));

type RequestRatingProps = {
  requestStatus: RatingStatus;
  entityId: string;
  refresh: () => void;
};

const RequestRating: FC<RequestRatingProps> = ({ requestStatus, entityId, refresh = null }) => {
  const { t } = useTranslation();
  const { requestEsgScore } = usePortfolioModals();

  return (
    <>
      <Typography display="block">
        {requestStatus === RatingStatus.TO_DO && t('ENTITY.UPLOAD_CUSTOM_DATA')}
        {requestStatus === RatingStatus.IN_PROGRESS && t('ENTITY.UPLOAD_CUSTOM_DATA')}
      </Typography>

      {requestStatus === RatingStatus.TO_DO && (
        <Box mt={1}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => requestEsgScore.show()}
          >
            {t('Request')}
          </Button>
        </Box>
      )}
      {requestStatus === RatingStatus.IN_PROGRESS && (
        <Box mt={1}>
          <Button variant="contained" size="small" color="primary" disabled>
            {t('Requested')}
          </Button>
        </Box>
      )}

      <RequestEsgScoreModal
        open={requestEsgScore.isOpen}
        onClose={() => requestEsgScore.close()}
        onRequested={refresh}
        selectIds={[entityId]}
      />
    </>
  );
};

type EmptyCustomEntityContentProps = {
  entityId: string;
  entityData: any;
  refreshEntity: () => void;
};

const EmptyCustomEntityContent: FC<EmptyCustomEntityContentProps> = ({
  entityId,
  entityData,
  refreshEntity,
}) => {
  const items = [
    { label: 'Industry', value: entityData.industry },
    { label: 'Region', value: entityData.region },
    { label: 'Subregion', value: entityData.subregion },
    { label: 'Country Code', value: entityData.countryCode },
  ];

  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <CompanyPanel
        title={entityData.name}
        items={items}
        activeModule=""
        onChangeModule={() => null}
        modules={[]}
      />

      <BlurWrapper
        active
        content={
          <div className={classes.requestBox}>
            <RequestRating
              requestStatus={entityData.meta.esgRequestState}
              entityId={entityId}
              refresh={refreshEntity}
            />
          </div>
        }
      >
        <ScoreWidget>
          <ScoreWidgetMainColumn
            accent={false}
            label={t('Score') as string}
            value={t('Score') as string}
            footer={t(`1 year PD`)}
            footerColor="white"
          />
          <ScoreWidgetSingleValueColumn
            label={t('Country Risk Assessment') as string}
            value={t('Country Risk Assessment') as string}
          />
          <ScoreWidgetSingleValueColumn
            label={t('Business Climate') as string}
            value={t('Business Climate') as string}
          />
          <ScoreWidgetSingleValueColumn
            label={t('Industry Risk Level') as string}
            value={t('Industry Risk Level') as string}
          />
          <ScoreWidgetSingleValueColumn
            label={t('Credit review date') as string}
            value={t('Credit review date') as string}
            last
          />
        </ScoreWidget>
      </BlurWrapper>
    </>
  );
};

export { EmptyCustomEntityContent };
